/**
 * Helpers partagés — thème NOMBRES 4ème (attendus 136-152, desc 11).
 *
 * Tout par construction : développement décimal EXACT par manipulation de
 * chaînes (aucune erreur de flottant), prédicats carrés parfaits / racines
 * entières, table des préfixes nano→giga, notation scientifique exacte.
 *
 * Revue 2026-09-23 : les helpers `exp` et `puiss`, qui fabriquaient des
 * exposants en caractères Unicode (« 10⁻³ »), sont SUPPRIMÉS. Toute puissance
 * affichée passe désormais par `puissTex` ou `tex` de `generators/utils.js`,
 * règle du dépôt : dès qu'une expression mathématique est lue par l'élève,
 * elle est rendue en KaTeX. Dans une phrase à trous, un trou en exposant
 * s'écrit avec son groupe protégé, `10^{{{-3}}}`.
 *
 * Convention de signe : les nombres et réponses négatifs saisis utilisent le
 * signe ASCII « - » (convention des gens relatifs).
 */

// ── Développement décimal EXACT (pas de flottant) ────────────────────

/**
 * Écriture décimale (chaîne SANS séparateur de milliers) d'une puissance de 10.
 *   dec10(0) = « 1 » ; dec10(6) = « 1000000 » ; dec10(-4) = « 0,0001 ».
 */
export const dec10 = (n) => {
    if (n === 0) return '1';
    if (n > 0) return '1' + '0'.repeat(n);
    return '0,' + '0'.repeat(-n - 1) + '1';
};

/**
 * Développe « mantisse × 10^exp » en écriture décimale EXACTE (chaîne).
 * @param {string} mant  mantisse à virgule française, ex. « 6,54 » ou « -3,2 »
 * @param {number} e     exposant entier (peut être négatif)
 * @returns {string} écriture décimale, virgule FR, sans zéros superflus.
 */
export const developpe = (mant, e) => {
    let s = String(mant).trim();
    const neg = s.startsWith('-');
    if (neg) s = s.slice(1);
    const [ent, dec = ''] = s.split(',');
    const digits = ent + dec;                 // « 654 »
    let point = ent.length + e;               // position de la virgule dans digits
    let out;
    if (point <= 0) out = '0,' + '0'.repeat(-point) + digits;
    else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length);
    else out = digits.slice(0, point) + ',' + digits.slice(point);
    if (out.includes(',')) out = out.replace(/0+$/, '').replace(/,$/, '');
    out = out.replace(/^0+(?=\d)/, '');       // pas de « 06540 »
    return (neg ? '-' : '') + out;
};

// ── Carrés parfaits et racines ───────────────────────────────────────

/** Carrés parfaits de 1 à 144 : { 1:1, 2:4, …, 12:144 }. */
export const CARRES = (() => { const o = {}; for (let n = 1; n <= 12; n++) o[n] = n * n; return o; })();

/** n est-il un carré parfait (entier ≥ 0 dont la racine est entière) ? */
export const estCarreParfait = (n) => {
    if (!Number.isInteger(n) || n < 0) return false;
    const r = Math.round(Math.sqrt(n));
    return r * r === n;
};

/** Racine carrée entière d'un carré parfait (Math.round anti-flottant). */
export const racineEntiere = (n) => Math.round(Math.sqrt(n));

// ── Préfixes nano → giga ─────────────────────────────────────────────

export const PREFIXES = [
    { nom: 'nano', sym: 'n', e: -9 },
    { nom: 'micro', sym: 'µ', e: -6 },
    { nom: 'milli', sym: 'm', e: -3 },
    { nom: 'centi', sym: 'c', e: -2 },
    { nom: 'déci', sym: 'd', e: -1 },
    { nom: 'déca', sym: 'da', e: 1 },
    { nom: 'hecto', sym: 'h', e: 2 },
    { nom: 'kilo', sym: 'k', e: 3 },
    { nom: 'méga', sym: 'M', e: 6 },
    { nom: 'giga', sym: 'G', e: 9 },
];

// ── Fractions ────────────────────────────────────────────────────────

export const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };

// ── Toolkit rationnel EXACT (cluster 3 « Calculer », attendus 146-152) ──
//
// Un rationnel = { p, q } avec q > 0, TOUJOURS réduit après opération. Toute
// l'arithmétique est entière (aucun flottant), les entrées restent < 2^53 pour
// nos tirages (numérateurs/dénominateurs ≤ quelques milliers). Convention de
// signe : le signe est porté par le numérateur, jamais par q.

/** Construit un rationnel réduit à partir de p/q (q peut être négatif). */
export const rat = (p, q = 1) => {
    if (q < 0) { p = -p; q = -q; }
    const g = pgcd(p, q) || 1;
    return { p: p / g, q: q / g };
};
export const ratAdd = (a, b) => rat(a.p * b.q + b.p * a.q, a.q * b.q);
export const ratSub = (a, b) => rat(a.p * b.q - b.p * a.q, a.q * b.q);
export const ratMul = (a, b) => rat(a.p * b.p, a.q * b.q);
export const ratDiv = (a, b) => rat(a.p * b.q, a.q * b.p);
/** Inverse (b/a) — a.p doit être ≠ 0 (0 n'a pas d'inverse). */
export const ratInv = (a) => rat(a.q, a.p);
export const ratOpp = (a) => ({ p: -a.p, q: a.q });
export const ratEq = (a, b) => a.p * b.q === b.p * a.q;

/**
 * Parse une écriture FR (« -2,5 », « 3/4 », « -7 ») en rationnel exact.
 * Accepte le moins Unicode « − » comme le moins ASCII.
 */
export const ratFromStr = (raw) => {
    const s = String(raw).trim().replace(/−/g, '-').replace(/\s/g, '');
    if (s.includes('/')) { const [a, b] = s.split('/'); return rat(parseInt(a, 10), parseInt(b, 10)); }
    if (s.includes(',')) {
        const neg = s.startsWith('-'); const t = neg ? s.slice(1) : s;
        const [i, d = ''] = t.split(',');
        return rat((neg ? -1 : 1) * parseInt((i + d) || '0', 10), 10 ** d.length);
    }
    return rat(parseInt(s, 10), 1);
};

/** Affichage fraction INLINE : { -3, 4 } → « -3/4 », { 5, 1 } → « 5 » (signe ASCII). */
export const fmtFrac = (r) => (r.q === 1 ? String(r.p) : `${r.p}/${r.q}`);

/**
 * Écriture décimale FR EXACTE d'un rationnel si le développement est fini
 * (dénominateur = 2^a·5^b), sinon null. Signe ASCII « - ».
 *   { -5, 2 } → « -2,5 » ; { 1, 3 } → null.
 */
export const decExact = (r) => {
    let q = r.q; let a = 0; let b = 0;
    while (q % 2 === 0) { q /= 2; a++; }
    while (q % 5 === 0) { q /= 5; b++; }
    if (q !== 1) return null;
    const k = Math.max(a, b);
    const num = r.p * (10 ** k) / r.q;          // entier exact
    if (k === 0) return String(num);
    const neg = num < 0; const abs = String(Math.abs(num)).padStart(k + 1, '0');
    const ent = abs.slice(0, abs.length - k);
    let dec = abs.slice(abs.length - k).replace(/0+$/, '');
    return (neg ? '-' : '') + (dec ? `${ent},${dec}` : ent);
};

/** Remplace le moins ASCII par le moins typographique « − » (AFFICHAGE seul). */
export const aff = (s) => String(s).replace(/-/g, '−');

// ── Puissances entières exactes (Number, jamais BigInt) ──────────────

/** base^n en entier/décimal exact (n ≥ 0). Reste < 2^53 pour nos tirages. */
export const pow = (base, n) => { let r = 1; for (let i = 0; i < n; i++) r *= base; return r; };

/** Chiffre des unités de base^n (n ≥ 1) via le cycle des unités. */
export const chiffreUnites = (base, n) => {
    const u = ((base % 10) + 10) % 10;
    const cycle = [];
    let x = u;
    do { cycle.push(x); x = (x * u) % 10; } while (x !== u && cycle.length < 4);
    return cycle[(n - 1) % cycle.length];
};
